'use client';

import styles from './plancard.module.scss';
import React, { useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import {
    ScrollableChannelTiles,
    ScrollableChannelTilesHandle,
} from '@/ui-components/molecules/ScrollableChannelTiles/ScrollableChannelTiles';
import { Button, ButtonProps } from '@/ui-components/atoms/Button/Button';
import { StarAnimation } from '@/ui-components/atoms/StarAnimation/StarAnimation';
import { Modal } from '@/ui-components/molecules/Modal/Modal';
import { PopularChannelsModal } from '../PopularChannelsModal/PopularChannelsModal';
import { Text } from '@/ui-components/atoms/Text/Text';
import { colorBrightness } from '@/packages/color/colorBrightness';
import { ChannelBadge } from '../ChannelCardStrip/ChannelBadge';
import { CSSPropsVars } from '@/interfaces/types/CSSPropsVars';
import { ShowMore } from '../ShowMore/ShowMore';
import useInheritParams from '@/src/hooks/useInheritParams';
import { URL_INHERIT_PARAMS } from '@/src/constants';
import { OfferChannels, PlanCardProps } from './PlanCard.types';
import { resolvePlanCardOfferSelection, withInheritedHref } from './resolvePlanCardOfferSelection';

const {
    cardHeading,
    planCard,
    headingSection,
    cardEyebrow,
    optionalItemWrapper,
    optionalItems,
    optionalItemHeading,
    optionalItemSubheading,
    toggleContainer,
    switchContainer,
    switchToggle,
    switchOn,
    circle,
    planPriceWrapper,
    badgeStyle,
    planCardFlag,
    whiteText,
    cardDescription,
    addOnLabel,
    bannerHidden,
} = styles;

// Keep in sync with the `max-height` of `.truncatedDescription` in plancard.module.scss
const DESCRIPTION_CLAMP_HEIGHT = 120;

interface BuildPlanOfferKeys {
    offer1Label?: string;
    offer2Label?: string;
    offer3Label?: string;
    offer1ExtraPrice?: string;
    offer2ExtraPrice?: string;
    offer3ExtraPrice?: string;
    [key: string]: string | undefined;
}

type StylesProps = React.CSSProperties & {
    '--flag-background': string;
};

export function PlanCard({ planCardContent, index = 0, allAccessPriceText }: PlanCardProps): React.JSX.Element {
    const inheritedPlanCtaHref = useInheritParams(planCardContent?.cta?.href || '', URL_INHERIT_PARAMS);

    const inheritedAllSelectedHref = useInheritParams(
        planCardContent?.buildPlan?.all_selected_offer_cta?.href || '',
        URL_INHERIT_PARAMS,
    );

    const inheritedOfferCtaHrefs = {
        offer1: useInheritParams(planCardContent?.buildPlan?.offer1Cta?.href || '', URL_INHERIT_PARAMS),
        offer2: useInheritParams(planCardContent?.buildPlan?.offer2Cta?.href || '', URL_INHERIT_PARAMS),
        offer3: useInheritParams(planCardContent?.buildPlan?.offer3Cta?.href || '', URL_INHERIT_PARAMS),
        offer12: useInheritParams(planCardContent?.buildPlan?.offer12Cta?.href || '', URL_INHERIT_PARAMS),
        offer13: useInheritParams(planCardContent?.buildPlan?.offer13Cta?.href || '', URL_INHERIT_PARAMS),
        offer23: useInheritParams(planCardContent?.buildPlan?.offer23Cta?.href || '', URL_INHERIT_PARAMS),
    };
    const [switches, setSwitches] = useState<Record<string, boolean>>({});
    const [selectedItems, setSelectedItems] = useState<Record<string, string>>({});
    const [priceText, setPriceText] = useState<React.ReactNode | null>(planCardContent?.planPriceText);
    const [selectedCta, setSelectedCta] = useState<ButtonProps | null>(
        withInheritedHref(planCardContent?.cta, inheritedPlanCtaHref),
    );
    const [selectedOfferChannels, setSelectedOfferChannels] = useState<OfferChannels | null>(
        planCardContent?.planCardChannels,
    );
    const [allAccessModalOpen, setAllAccessModalOpen] = useState(false);
    const [popularChannelModalOpen, setPopularChannelModalOpen] = useState(false);
    const [startAnimation, setStartAnimation] = useState(false);
    const [fadeOut, setFadeOut] = useState(false);
    const [fadeTiles, setFadeTiles] = useState(false);
    const [showMoreOpen, setShowMoreOpen] = useState(false);
    const [isDescriptionOverflowing, setIsDescriptionOverflowing] = useState(false);
    const descriptionRef = useRef<HTMLDivElement>(null);
    const modalContainerRef = useRef<HTMLDivElement>(null);
    const scrolableTilesWrapperRef = useRef<HTMLDivElement>(null);
    const buttonRefs = useRef<(HTMLDivElement | null)[]>([]);
    const childRef = useRef<ScrollableChannelTilesHandle>(null);
    const hasAnimatedRef = useRef(false);

    const cardFlagStyles: React.CSSProperties = {
        '--flag-background': planCardContent?.cardFlagColor,
    } as StylesProps;

    const allAccessModalCloseHandler = () => {
        setStartAnimation(false);
        setFadeOut(false);
        setSelectedItems({});
        setSwitches({});
        setAllAccessModalOpen(false);
        setSelectedCta(withInheritedHref(planCardContent?.cta, inheritedPlanCtaHref));
        setPriceText(planCardContent?.planPriceText);
        setSelectedOfferChannels(planCardContent?.planCardChannels);
    };
    const toggleSwitch = (key: string, price: number, addOnName: string) => {
        setFadeTiles(true);
        setTimeout(() => {
            setSwitches((prev) => {
                const isCurrentlyOn = !!prev[key];
                const updatedSwitches = {
                    ...prev,
                    [key]: !isCurrentlyOn,
                };
                setSelectedItems((prevItems) => {
                    const updatedItems = { ...prevItems };
                    if (isCurrentlyOn) {
                        delete updatedItems[key];
                    } else {
                        updatedItems[key] = addOnName;
                    }
                    return updatedItems;
                });
                const offerSelection = resolvePlanCardOfferSelection({
                    switches: updatedSwitches,
                    planCardContent,
                    allAccessPriceText,
                    previousCta: selectedCta,
                    inheritedHrefs: {
                        base: inheritedPlanCtaHref,
                        allSelected: inheritedAllSelectedHref,
                        ...inheritedOfferCtaHrefs,
                    },
                });

                setSelectedCta(offerSelection.cta);
                setPriceText(offerSelection.priceText);
                setSelectedOfferChannels(offerSelection.offerChannels);

                return updatedSwitches;
            });
        }, 100);
    };

    const extractOffers = (fields: BuildPlanOfferKeys) => {
        if (!fields) {
            return [];
        }
        const offers = [];

        for (let i = 1; i <= 3; i++) {
            const labelKey = `offer${i}Label`;
            const priceKey = `offer${i}ExtraPrice`;

            const label = fields[labelKey];
            if (label) {
                offers.push({
                    key: `offer${i}`,
                    item: label,
                    pricing: fields[priceKey] || '',
                    addOnPrice: parseFloat((fields[priceKey] || '').replace(/[^0-9.]/g, '')) || 0,
                });
            }
        }

        return offers;
    };
    const offerFields = {
        offer1Label: planCardContent?.buildPlan?.offer1Label,
        offer2Label: planCardContent?.buildPlan?.offer2Label,
        offer3Label: planCardContent?.buildPlan?.offer3Label,
        offer1ExtraPrice: planCardContent?.buildPlan?.offer1ExtraPrice,
        offer2ExtraPrice: planCardContent?.buildPlan?.offer2ExtraPrice,
        offer3ExtraPrice: planCardContent?.buildPlan?.offer3ExtraPrice,
    };
    const offers = extractOffers(offerFields as BuildPlanOfferKeys);

    const hasHiddenChannels = (selectedOfferChannels?.popularChannels?.length ?? 0) > 0;
    const hasTruncatedDescription = !offers?.length && isDescriptionOverflowing;
    const showMoreAvailable = !planCardContent?.hideShowMore && (hasHiddenChannels || hasTruncatedDescription);

    const handleViewMoreClick = () => {
        setPopularChannelModalOpen(true);
    };

    const handleClosePopularChannelModal = () => {
        setPopularChannelModalOpen(false);
    };

    const handleAllSelectedCtaClick = () => {
        setAllAccessModalOpen(true);
    };

    useEffect(() => {
        const el = descriptionRef.current;
        if (!el || typeof ResizeObserver === 'undefined') {
            return;
        }

        const measure = () => {
            setIsDescriptionOverflowing(el.scrollHeight > DESCRIPTION_CLAMP_HEIGHT);
        };

        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, [planCardContent?.description]);

    useEffect(() => {
        if (Object.keys(selectedItems)?.length === 3) {
            const timer = setTimeout(() => {
                setAllAccessModalOpen(true);
            }, 600);

            return () => clearTimeout(timer);
        }
    }, [selectedItems]);

    useEffect(() => {
        const scrolableTilesWrapper = scrolableTilesWrapperRef.current;

        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (entry.isIntersecting && !hasAnimatedRef.current) {
                    childRef.current?.animateOneTile?.();
                    hasAnimatedRef.current = true;
                }
            },
            { threshold: 0.5 },
        );
        if (scrolableTilesWrapper) {
            observer.observe(scrolableTilesWrapper);
        }
        return () => {
            if (scrolableTilesWrapper) {
                observer.unobserve(scrolableTilesWrapper);
            }
        };
    }, []);

    const selectedValues = Object.values(selectedItems);
    const planCardDataComponentType =
        selectedValues.length === 0
            ? `PlanCard-${planCardContent?.title}`
            : `PlanCard-${planCardContent?.title}-${selectedValues.join('-')}`;
    let modalPageName =
        selectedValues.length === 0 ? planCardContent?.title : `${planCardContent?.title}-${selectedValues.join('-')}`;
    modalPageName = modalPageName.replace(/(\s|:)/g, '').toLowerCase();

    const brightness = colorBrightness(planCardContent?.cardFlagColor);

    const borderStyle: CSSPropsVars = {
        ...(!planCardContent?.blurredBackground && selectedCta?.color && { '--card-border-color': selectedCta?.color }),
    };

    return (
        <div
            className={cn(planCard, {
                [bannerHidden]: planCardContent?.hideBanner,
                [styles.blurredBackground]: planCardContent?.blurredBackground,
            })}
            style={borderStyle}
            key={index}
            ref={modalContainerRef}
            data-componenttype={planCardDataComponentType}
            data-cy="plan-card"
        >
            {!planCardContent?.hideBanner && (
                <div
                    className={cn(planCardFlag, {
                        [whiteText]: brightness < 128,
                    })}
                    style={cardFlagStyles}
                >
                    <Text tag="p">{planCardContent?.cardFlagText}</Text>
                </div>
            )}
            <div className={headingSection}>
                <span className={cardEyebrow}>{planCardContent?.eyebrow}</span>
                <span className={cardHeading}>{planCardContent?.title}</span>
                {planCardContent?.listeningOptionsLabel && !planCardContent?.hideBadge && (
                    <ChannelBadge
                        badgeText={planCardContent?.listeningOptionsLabel}
                        className={cn(badgeStyle, {
                            [styles.blurredBackgroundBadge]: planCardContent?.blurredBackground,
                        })}
                    />
                )}
                <div
                    ref={descriptionRef}
                    className={cn(cardDescription, {
                        [styles.truncatedDescription]:
                            !offers?.length &&
                            !showMoreOpen &&
                            !planCardContent?.hideShowMore &&
                            isDescriptionOverflowing,
                        [styles.checkmarkList]:
                            planCardContent?.listStyle === 'Checkmark' && planCardContent?.blurredBackground,
                        [styles.bulletList]:
                            planCardContent?.listStyle === 'Bullet' && planCardContent?.blurredBackground,
                        [styles.noList]: planCardContent?.listStyle === 'None' && planCardContent?.blurredBackground,
                    })}
                >
                    <Text tag="div">{planCardContent?.description}</Text>
                    {!offers?.length && !showMoreOpen && !planCardContent?.hideShowMore && isDescriptionOverflowing && (
                        <div className={styles.descriptionFade} />
                    )}
                </div>
            </div>
            {offers?.length > 0 && (
                <>
                    {planCardContent?.buildPlan?.addOnLabel && (
                        <span className={addOnLabel}>{planCardContent?.buildPlan?.addOnLabel}</span>
                    )}
                    <div
                        className={cn(optionalItemWrapper, {
                            [styles.animatePosition]: startAnimation,
                            [styles.fadeOut]: fadeOut,
                        })}
                    >
                        {offers?.map((op, index) => (
                            <div className={optionalItems} key={index}>
                                <span className={optionalItemHeading}>{op.item}</span>
                                <span className={optionalItemSubheading}>{op.pricing}</span>
                                <div className={toggleContainer}>
                                    <div
                                        className={switchContainer}
                                        ref={(el) => {
                                            buttonRefs.current[index] = el;
                                        }}
                                    >
                                        <button
                                            type="button"
                                            aria-pressed={switches[op.key] ? 'true' : 'false'}
                                            aria-label={`${op.item} is ${switches[op.key] ? 'On' : 'Off'}`}
                                            data-linkname={`${op.item} is ${switches[op.key] ? 'Off' : 'On'}`}
                                            className={cn(switchToggle, {
                                                [switchOn]: switches[op.key],
                                            })}
                                            onClick={() => toggleSwitch(op.key, op.addOnPrice, op.item)}
                                        >
                                            <div className={circle} />
                                        </button>
                                        <StarAnimation animate={switches[op.key]} />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}

            {showMoreAvailable && (
                <div className={styles.showMoreWrapper}>
                    <ShowMore
                        id={`plancard-showmore-${index}`}
                        showText="Show more"
                        hideText="Show less"
                        isContentShown={showMoreOpen}
                        onClickHandler={() => setShowMoreOpen((prev) => !prev)}
                        color={'var(--base-contrast, #1E2226)'}
                    />
                </div>
            )}
            {!planCardContent?.underCTA && (
                <div className={planPriceWrapper}>
                    <Text tag="p">{priceText}</Text>
                </div>
            )}
            <div
                className={cn(styles.buttonWrapper, {
                    [styles.buttonWrapperUnderCTA]: planCardContent?.underCTA,
                })}
            >
                {Object.keys(selectedItems)?.length === 3 && index === 1 ? (
                    <Button
                        {...selectedCta}
                        type="button"
                        variant={selectedCta?.variant || 'primary'}
                        ariaLabel={selectedCta?.ariaLabel}
                        className={styles.button}
                        onClick={handleAllSelectedCtaClick}
                    >
                        {selectedCta?.children}
                    </Button>
                ) : (
                    <Button
                        {...selectedCta}
                        type="button"
                        variant={selectedCta?.variant || 'primary'}
                        ariaLabel={selectedCta?.ariaLabel}
                        className={styles.button}
                    />
                )}
            </div>
            {planCardContent?.underCTA && (
                <div
                    className={cn(planPriceWrapper, {
                        [styles.planPriceWrapperUnderCTA]: planCardContent?.underCTA,
                    })}
                >
                    <Text tag="p">{priceText}</Text>
                </div>
            )}
            <div
                className={cn(styles.expandableContent, {
                    [styles.hiddenContent]: !showMoreOpen && !planCardContent?.hideShowMore,
                })}
            >
                {selectedOfferChannels?.popularChannels && selectedOfferChannels?.popularChannels?.length > 0 && (
                    <div
                        ref={index === 0 ? scrolableTilesWrapperRef : null}
                        className={cn(styles.tilesFadeWrapper, {
                            [styles.tilesFadeOut]: fadeTiles,
                        })}
                        onTransitionEnd={() => setFadeTiles(false)}
                    >
                        <ScrollableChannelTiles
                            items={selectedOfferChannels?.popularChannels || []}
                            extraTileColor={selectedOfferChannels?.extraTileColor}
                            extraTileLabel={selectedOfferChannels?.extraTileLabel}
                            handleViewMoreClick={handleViewMoreClick}
                            ref={childRef}
                        />
                    </div>
                )}

                {selectedOfferChannels?.popularChannels && selectedOfferChannels?.popularChannels?.length > 0 && (
                    <div className={styles.viewMore}>
                        <button onClick={handleViewMoreClick} aria-label={selectedOfferChannels?.channelsLinkLabel}>
                            {selectedOfferChannels?.channelsLinkLabel}
                        </button>
                    </div>
                )}
            </div>

            <Modal
                headline="Looks like you want the complete SiriusXM experience "
                subhead="Subscribe to All Access to get all 3 add-ons—and save."
                ctas={
                    [
                        {
                            ...planCardContent?.buildPlan?.all_selected_offer_cta,
                            href: inheritedAllSelectedHref,
                            variant: planCardContent?.buildPlan?.all_selected_offer_cta?.variant || 'primary',
                            type: 'button',
                        },
                    ] as ButtonProps[]
                }
                variant="plancard"
                open={allAccessModalOpen}
                onClose={allAccessModalCloseHandler}
                containerRef={modalContainerRef}
                dataLocalpagename={'overlay:allaccessmodal'}
            />
            {popularChannelModalOpen && (
                <PopularChannelsModal
                    open={popularChannelModalOpen}
                    onClose={handleClosePopularChannelModal}
                    tabs={selectedOfferChannels?.channels?.tabs || []}
                    heading={selectedOfferChannels?.channels?.title}
                    dataLocalpagename={`overlay:${modalPageName}`}
                />
            )}
        </div>
    );
}
